import { BaseCropperController } from "./lib/image_cropper/base_cropper_controller"

/*
 * CardTypeImageCropperController
 *
 * Stimulus controller for managing card type images (Apple Wallet and
 * Google Wallet passes). Extends BaseCropperController with specific
 * configuration for four image types.
 *
 * Image types:
 *   - square: 1:1 logo (Apple icon + Google logo)
 *   - wide:   Free ratio logo (Apple logo, optional)
 *   - strip:  ~3:1 strip image (Apple only)
 *   - hero:   Free ratio hero image (Google only)
 */
export default class extends BaseCropperController {
  static targets = [
    "squarePreview", "squareCropButton", "squareModal", "squareModalImage",
    "squareCropData", "squarePreviewData", "squareOriginalData",
    "squareSourceInput", "squareCroppedInput", "squareSourceUrl",
    "widePreview", "wideCropButton", "wideModal", "wideModalImage",
    "wideCropData", "widePreviewData", "wideOriginalData",
    "wideSourceInput", "wideCroppedInput", "wideSourceUrl",
    "stripPreview", "stripCropButton", "stripModal", "stripModalImage",
    "stripCropData", "stripPreviewData", "stripOriginalData",
    "stripSourceInput", "stripCroppedInput", "stripSourceUrl",
    "heroPreview", "heroCropButton", "heroModal", "heroModalImage",
    "heroCropData", "heroPreviewData", "heroOriginalData",
    "heroSourceInput", "heroCroppedInput", "heroSourceUrl"
  ]

  /*
   * imageTypes
   *
   * List of image type identifiers handled by this controller.
   */
  static imageTypes = ["square", "wide", "strip", "hero"]

  /*
   * imageConfig
   *
   * Configuration for each image type. All use PNG format with transparent
   * padding for consistent appearance on wallet passes.
   */
  static imageConfig = {
    square: {
      maxWidth: 1320,
      maxHeight: 1320,
      aspectRatio: 1,
      format: "png",
      backgroundColor: "transparent"
    },
    wide: {
      maxWidth: 940,
      maxHeight: 300,
      aspectRatio: NaN,
      format: "png",
      backgroundColor: "transparent"
    },
    strip: {
      maxWidth: 2250,
      maxHeight: 738,
      aspectRatio: 2250 / 738,
      format: "png",
      backgroundColor: "transparent"
    },
    hero: {
      maxWidth: 2064,
      maxHeight: 672,
      aspectRatio: NaN,
      format: "png",
      backgroundColor: "transparent"
    }
  }

  /*
   * getTargetsForType
   *
   * Returns all target elements for a given image type.
   *
   * Input:
   *   type {string} - Image type identifier.
   *
   * Output:
   *   {object} - Object containing all target elements for this type.
   */
  getTargetsForType(type) {
    return {
      preview: this[`${type}PreviewTarget`],
      cropButton: this[`${type}CropButtonTarget`],
      modal: this[`${type}ModalTarget`],
      modalImage: this[`${type}ModalImageTarget`],
      cropData: this[`${type}CropDataTarget`],
      previewData: this[`${type}PreviewDataTarget`],
      originalData: this[`${type}OriginalDataTarget`],
      sourceInput: this[`${type}SourceInputTarget`],
      croppedInput: this[`${type}CroppedInputTarget`],
      sourceUrl: this[`${type}SourceUrlTarget`],
      originalUrl: null // Card type controller doesn't use separate original URL
    }
  }

  /*
   * _dispatchUpdateEvent
   *
   * Dispatches the card-type-specific update event for the preview controller.
   *
   * Input:
   *   type {string} - Image type identifier.
   */
  _dispatchUpdateEvent(type) {
    this.element.dispatchEvent(
      new CustomEvent("card-type-image-update", {
        bubbles: true,
        detail: { type }
      })
    )
  }
}
